'use client'

import { Box, Text, VStack, HStack, Button, Flex, Grid } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { PositionCard } from './PositionCard'
import { HexString } from '@/lib/config/wagmi'
import { useHLClearingHouseState } from '@/lib/hooks/useMargin'
import { LoadingState } from '../common/LoadingState'
import { useMemo } from 'react'
import TouchableOpacity from '../ui/TouchableOpacity'

interface PositionSectionProps {
  userAddress: HexString
  onViewAll?: () => void
  hideHeader?: boolean
}

interface Position {
  id: string
  symbol: string
  side: 'LONG' | 'SHORT'
  leverage: string
  entryPrice: string
  pnl: string
  pnlPercent: string
  amount: string
  marketPrice: string
  liquidationPrice: string
}

export function PositionSection({ userAddress, onViewAll, hideHeader }: PositionSectionProps) {
  const t = useTranslations()

  const { data: clearingHouseState, isLoading, error } = useHLClearingHouseState({ user: userAddress })

  // Filter out positions with zero size
  const activePositions = useMemo(() => {
    return clearingHouseState?.assetPositions.filter(
      assetPosition => Number.parseFloat(assetPosition.position.szi) !== 0,
    ) || []
  }, [clearingHouseState])

  if (isLoading) {
    return (
      <Box p={4}>
        {!hideHeader && (
          <HStack mb={4} align="center" justify="space-between">
            <Text fontSize="sm" color="text.t1/90" fontWeight="bold">{t('trader.positions.openPositions')}</Text>
          </HStack>
        )}
        <LoadingState />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={4}>
        {!hideHeader && (
          <HStack mb={4} align="center" justify="space-between">
            <Text fontSize="sm" color="text.t1/90" fontWeight="bold">{t('trader.positions.openPositions')}</Text>
          </HStack>
        )}
        <Box rounded="xl" bg="white/5" p={4}>
          <Text textAlign="center" fontSize="sm" color="red.400">{t('trader.position.error')}</Text>
        </Box>
      </Box>
    )
  }

  if (activePositions.length === 0) {
    return (
      <Box p={4}>
        {!hideHeader && (
          <HStack mb={4} align="center" justify="space-between">
            <Text fontSize="sm" color="text.t1/90" fontWeight="bold">{t('trader.positions.openPositions')}</Text>
          </HStack>
        )}
        <Box rounded="xl" bg="white/5" p={4}>
          <Text textAlign="center" fontSize="sm" color="white/60">{t('trader.position.noPositions')}</Text>
        </Box>
      </Box>
    )
  }

  return (
    <Box p={4} pt={hideHeader ? 0 : 4} h={hideHeader ? 'full' : 'auto'} overflow="auto">
      {/* Header */}
      {!hideHeader && (
        <HStack mb={4} align="center" justify="space-between">
          <Text fontSize="sm" color="text.t1/90" fontWeight="bold">{t('trader.positions.openPositions')}</Text>

          <TouchableOpacity onClick={onViewAll}>
            <Text color="text.expand" fontSize="xs" fontWeight="medium">
              {t('trader.viewAll', { count: activePositions.length })}
            </Text>
          </TouchableOpacity>
        </HStack>
      )}

      {/* Position Cards */}
      <Grid gap={4} templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}>
        {hideHeader
          ? activePositions.map(assetPosition => (
              <Box key={`${assetPosition.position.coin}-${assetPosition.position.szi}`} w="full">
                <PositionCard position={assetPosition} userAddress={userAddress} />
              </Box>
            ))
          : activePositions.slice(0, 3).map((assetPosition) => (
              <Box key={`${assetPosition.position.coin}-${assetPosition.position.szi}`} w="full">
                <PositionCard position={assetPosition} userAddress={userAddress} />
              </Box>
            ))}
      </Grid>
    </Box>
  )
}